// src/Leaderboard.js
import React, { useEffect, useState, useMemo } from "react";
import { supabase } from "./supabaseClient";

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

const tableStyle = {
  borderCollapse: "collapse",
  width: "100%",
  fontFamily: "system-ui, -apple-system, sans-serif",
  minWidth: "350px",
  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
  borderRadius: "8px",
  overflow: "hidden",
};

const thTdStyle = {
  border: "1px solid #e5e7eb",
  padding: "12px 16px",
  textAlign: "left",
};

const thStyle = {
  backgroundColor: "#f9fafb",
  fontWeight: "600",
  color: "#374151",
};

const Leaderboard = ({ teams = [] }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState(null);

  // Get current logged-in user ID
  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) setCurrentUserId(user.id);
    });
  }, []);

  // Build dual-key lookup map (maps both id and name to price)
  const priceMap = useMemo(() => {
    const map = {};
    teams.forEach((team) => {
      const price = team.price || 0;
      if (team.id) map[team.id] = price;
      if (team.name) map[team.name] = price;
    });
    return map;
  }, [teams]);

  // Real-time listener for user profiles in Supabase
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const { data, error } = await supabase.from("profiles").select("*");
        if (error) throw error;
        setUsers(data || []);
      } catch (error) {
        console.error("Error fetching leaderboard users:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();

    const channel = supabase
      .channel("public:profiles:leaderboard")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "profiles" },
        () => {
          fetchUsers();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // Calculate and sort rankings whenever users or team prices change
  const leaderboard = useMemo(() => {
    return users
      .map((user) => {
        const portfolio = user.portfolio || {};
        const cash = user.cash || 0;

        const portfolioValue = Object.entries(portfolio).reduce(
          (sum, [key, shares]) => sum + (shares || 0) * (priceMap[key] || 0),
          0
        );

        const nameFallback =
          user.username ||
          user.displayName ||
          (user.email ? user.email.split("@")[0] : null) ||
          `Trader #${user.id ? user.id.slice(0, 5) : "User"}`;

        return {
          userId: user.id,
          username: nameFallback,
          cash,
          portfolioValue,
          totalNetWorth: cash + portfolioValue,
        };
      })
      .sort((a, b) => b.totalNetWorth - a.totalNetWorth);
  }, [users, priceMap]);

  const renderRankBadge = (index) => {
    if (index === 0) return "🥇 1st";
    if (index === 1) return "🥈 2nd";
    if (index === 2) return "🥉 3rd";
    return `#${index + 1}`;
  };

  return (
    <div
      style={{
        padding: "1.5rem",
        maxWidth: "1200px",
        margin: "0 auto",
        paddingBottom: "calc(1.5rem + env(safe-area-inset-bottom))",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "1.25rem",
        }}
      >
        <h2 style={{ margin: 0 }}>Leaderboard</h2>
        <span
          style={{
            fontSize: "0.85rem",
            color: "#2b8a3e",
            backgroundColor: "#ebfbee",
            padding: "4px 10px",
            borderRadius: "12px",
            fontWeight: "600",
            border: "1px solid #b2f2bb",
          }}
        >
          ● Live Rankings
        </span>
      </div>

      {loading ? (
        <p style={{ color: "#6b7280" }}>Loading leaderboard standings...</p>
      ) : leaderboard.length === 0 ? (
        <p style={{ color: "#6b7280" }}>No registered traders found yet.</p>
      ) : (
        <div
          style={{
            width: "100%",
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={{ ...thTdStyle, ...thStyle, width: "90px" }}>Rank</th>
                <th style={{ ...thTdStyle, ...thStyle }}>Trader</th>
                <th style={{ ...thTdStyle, ...thStyle }}>Net Worth</th>
              </tr>
            </thead>
            <tbody>
              {leaderboard.map((entry, index) => {
                const isCurrentUser = entry.userId === currentUserId;

                return (
                  <tr
                    key={entry.userId}
                    style={{
                      backgroundColor: isCurrentUser ? "#eef6ff" : "transparent",
                      fontWeight: isCurrentUser ? "600" : "normal",
                    }}
                  >
                    <td style={thTdStyle}>
                      <span
                        style={{
                          fontWeight: index < 3 ? "bold" : "normal",
                          color: index === 0 ? "#d97706" : "inherit",
                        }}
                      >
                        {renderRankBadge(index)}
                      </span>
                    </td>
                    <td style={thTdStyle}>
                      {entry.username}
                      {isCurrentUser && (
                        <span
                          style={{
                            marginLeft: "8px",
                            fontSize: "0.75rem",
                            backgroundColor: "#2563eb",
                            color: "#fff",
                            padding: "2px 6px",
                            borderRadius: "4px",
                          }}
                        >
                          YOU
                        </span>
                      )}
                    </td>
                    <td
                      style={{
                        ...thTdStyle,
                        fontWeight: "bold",
                        color: "#2b8a3e",
                      }}
                    >
                      {currencyFormatter.format(entry.totalNetWorth)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Leaderboard;